/**
 * Capture lesson content as a complete, multi-page A4 PDF (html2canvas + jsPDF).
 * Generates an end-to-end printable study guide containing all sections, cards,
 * interactive questions with answers, learning objectives, and workplace scenarios.
 */

import { marked } from "marked";
import type { LessonContent, LessonSection } from "@/lib/lesson-content/schema";
import {
  isProfessionalProactiveStructuredData,
  type ProfessionalProactiveStructuredData,
} from "@/components/student/ProfessionalProactiveLessonContent";
import {
  isProactiveStructuredData,
  type ProactiveStructuredData,
} from "@/components/student/ProactiveLessonContent";

export function lessonPdfFilename(courseTitle: string, lessonTitle: string): string {
  const safe = (s: string) =>
    s
      .replace(/[/\\?%*:|"<>]/g, "-")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80) || "lesson";
  return `${safe(courseTitle)} — ${safe(lessonTitle)}.pdf`;
}

export interface LessonPdfExportParams {
  courseTitle: string;
  lessonTitle: string;
  moduleTitle?: string;
  industryDomain?: string | null;
  language?: string;
  blocksContent?: LessonContent | null;
  structuredContent?: unknown;
  fallbackRoot?: HTMLElement | null;
}

function renderMarkdown(md: string): string {
  try {
    return marked.parse(md, { gfm: true, breaks: true }) as string;
  } catch {
    return `<p>${escapeHtml(md)}</p>`;
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function buildSectionBlocksHtml(section: LessonSection, sectionIdx: number): string {
  const title = section.title?.trim() || `Part ${sectionIdx + 1}`;
  let html = `<div class="pdf-section">
    <h2 class="pdf-section-title">Part ${sectionIdx + 1}: ${escapeHtml(title)}</h2>
    <div class="pdf-blocks">`;

  for (const block of section.blocks) {
    const data = block.data as Record<string, unknown>;
    switch (block.type as string) {
      case "rich_text":
      case "markdown": {
        const md = typeof data?.markdown === "string" ? data.markdown : typeof data?.content === "string" ? data.content : "";
        if (md.trim()) {
          html += `<div class="pdf-block pdf-prose">${renderMarkdown(md)}</div>`;
        }
        break;
      }
      case "image": {
        const url = String(data?.image_url || data?.url || "");
        const alt = String(data?.alt || "");
        const caption = typeof data?.caption === "string" ? data.caption : "";
        if (url) {
          html += `<div class="pdf-block pdf-image-box">
            <img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}" style="max-width: 100%; height: auto; border-radius: 8px; margin: 10px 0; display: block;" />
            ${caption ? `<p class="pdf-image-caption" style="font-size: 11px; color: #64748b; margin-top: 4px; font-style: italic;">${escapeHtml(caption)}</p>` : ""}
          </div>`;
        }
        break;
      }
      case "flip_cards": {
        const prompt = typeof data?.prompt === "string" ? data.prompt : "Key terms and concepts:";
        const cards = Array.isArray(data?.cards) ? data.cards : [];
        html += `<div class="pdf-block pdf-card-group">
          <p class="pdf-card-prompt"><strong>${escapeHtml(prompt)}</strong></p>
          <div class="pdf-cards-list">`;
        for (const card of cards) {
          const front = String(card?.front || card?.term || "");
          const back = String(card?.back || card?.definition || "");
          html += `<div class="pdf-card-item">
            <div class="pdf-card-term"><strong>${escapeHtml(front)}</strong></div>
            <div class="pdf-card-desc">${renderMarkdown(back)}</div>
          </div>`;
        }
        html += `</div></div>`;
        break;
      }
      case "mcq":
      case "quiz": {
        const prompt =
          typeof data?.question === "string"
            ? data.question
            : typeof data?.prompt === "string"
            ? data.prompt
            : "Knowledge Check";
        const options = Array.isArray(data?.options) ? data.options : [];
        const correctIndex = typeof data?.correct_index === "number" ? data.correct_index : -1;
        const explanation = typeof data?.explanation === "string" ? data.explanation : "";
        html += `<div class="pdf-block pdf-quiz-box">
          <p class="pdf-quiz-prompt"><strong>Q: ${escapeHtml(prompt)}</strong></p>
          <ul class="pdf-quiz-options">`;
        for (let idx = 0; idx < options.length; idx++) {
          const opt = options[idx];
          const optText = typeof opt === "string" ? opt : String(opt?.text || "");
          const isCorrect = (typeof opt === "object" && Boolean(opt?.correct)) || idx === correctIndex;
          html += `<li class="${isCorrect ? "pdf-opt-correct" : "pdf-opt"}">${isCorrect ? "✓ " : "• "}${escapeHtml(optText)} ${isCorrect ? "<em>(Correct Answer)</em>" : ""}</li>`;
        }
        html += `</ul>`;
        if (explanation.trim()) {
          html += `<div class="pdf-quiz-exp"><strong>Key Takeaway:</strong> ${renderMarkdown(explanation)}</div>`;
        }
        html += `</div>`;
        break;
      }
      case "categorization": {
        const prompt = typeof data?.prompt === "string" ? data.prompt : "Categorization Exercise";
        const buckets = Array.isArray(data?.buckets)
          ? data.buckets
          : Array.isArray(data?.categories)
          ? data.categories
          : [];
        const items = Array.isArray(data?.items) ? data.items : [];
        html += `<div class="pdf-block pdf-cat-box">
          <p class="pdf-cat-prompt"><strong>${escapeHtml(prompt)} (Solved)</strong></p>
          <div class="pdf-cat-grid">`;
        for (const bucket of buckets) {
          const bucketId = String(bucket?.id || "");
          const bucketLabel = String(bucket?.label || bucketId || "Category");
          const bucketItems = items.filter((it) => (it?.correct_bucket_id || it?.category_id) === bucketId);
          html += `<div class="pdf-cat-col">
            <h4 class="pdf-cat-name">${escapeHtml(bucketLabel)}</h4>
            <ul>`;
          for (const it of bucketItems) {
            const itemText = String(it?.label || it?.text || "");
            html += `<li>✓ ${escapeHtml(itemText)}</li>`;
          }
          html += `</ul></div>`;
        }
        html += `</div></div>`;
        break;
      }
      case "sequencing": {
        const prompt = typeof data?.prompt === "string" ? data.prompt : "Order of Process";
        const rawItems = Array.isArray(data?.items) ? data.items : [];
        const correctOrder = Array.isArray(data?.correct_order) ? (data.correct_order as string[]) : [];

        let sortedItems = rawItems;
        if (correctOrder.length > 0) {
          const itemMap = new Map<string, any>();
          for (const it of rawItems) {
            if (it?.id) itemMap.set(it.id, it);
          }
          const ordered = correctOrder.map((id) => itemMap.get(id)).filter(Boolean);
          if (ordered.length > 0) {
            sortedItems = ordered;
          }
        }

        html += `<div class="pdf-block pdf-seq-box">
          <p class="pdf-seq-prompt"><strong>${escapeHtml(prompt)} (Correct Sequence)</strong></p>
          <ol class="pdf-seq-list">`;
        for (const it of sortedItems) {
          const itText = typeof it === "string" ? it : String(it?.label || it?.text || "");
          html += `<li>${escapeHtml(itText)}</li>`;
        }
        html += `</ol></div>`;
        break;
      }
      case "fill_blank": {
        const template = typeof data?.template === "string" ? data.template : "";
        const blanks = Array.isArray(data?.blanks) ? data.blanks : [];
        const blankMap = new Map<string, string>();
        for (const b of blanks) {
          const bId = String(b?.id || "");
          const opts = Array.isArray(b?.options) ? b.options : [];
          const correctIdx = typeof b?.correct_index === "number" ? b.correct_index : 0;
          const correctVal = String(opts[correctIdx] || "");
          blankMap.set(bId, correctVal);
        }

        let solvedTemplate = template;
        for (const [bId, val] of blankMap.entries()) {
          const placeholderRegex = new RegExp(`\\{\\{${bId}\\}\\}`, "g");
          solvedTemplate = solvedTemplate.replace(
            placeholderRegex,
            `<strong><u>${val}</u></strong>`
          );
        }

        html += `<div class="pdf-block pdf-fill-blank-box">
          <p class="pdf-fill-prompt"><strong>Fill in the Blanks (Completed)</strong></p>
          <div class="pdf-prose pdf-fill-body">${renderMarkdown(solvedTemplate)}</div>
          ${blankMap.size > 0 ? `<div class="pdf-fill-keys"><span class="pdf-fill-keys-title"><strong>Solved answers: </strong></span>${[...blankMap.entries()].map(([k, v]) => `<strong>${escapeHtml(v)}</strong>`).join(", ")}</div>` : ""}
        </div>`;
        break;
      }
      case "accordion_tabs": {
        const sections = Array.isArray(data?.sections) ? data.sections : [];
        html += `<div class="pdf-block pdf-accordion-group">`;
        for (const sec of sections) {
          const secTitle = String(sec?.title || "");
          const secBody = String(sec?.body || "");
          html += `<div class="pdf-accordion-item">
            <h4 class="pdf-accordion-title">${escapeHtml(secTitle)}</h4>
            <div class="pdf-prose">${renderMarkdown(secBody)}</div>
          </div>`;
        }
        html += `</div>`;
        break;
      }
      case "stepped_timeline": {
        const steps = Array.isArray(data?.steps) ? data.steps : [];
        html += `<div class="pdf-block pdf-timeline-box">
          <div class="pdf-timeline-list">`;
        for (let i = 0; i < steps.length; i++) {
          const step = steps[i];
          const stepLabel = String(step?.label || `Step ${i + 1}`);
          const stepBody = String(step?.body || "");
          html += `<div class="pdf-timeline-item">
            <div class="pdf-timeline-num">${i + 1}</div>
            <div class="pdf-timeline-content">
              <h4 class="pdf-timeline-title">${escapeHtml(stepLabel)}</h4>
              <div class="pdf-prose">${renderMarkdown(stepBody)}</div>
            </div>
          </div>`;
        }
        html += `</div></div>`;
        break;
      }
      case "image_hotspot": {
        const url = String(data?.image_url || "");
        const alt = String(data?.alt || "");
        const hotspots = Array.isArray(data?.hotspots) ? data.hotspots : [];
        html += `<div class="pdf-block pdf-hotspot-box">`;
        if (url) {
          html += `<img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}" class="pdf-hotspot-img" />`;
        }
        if (hotspots.length > 0) {
          html += `<div class="pdf-hotspot-list">
            <h4 class="pdf-hotspot-header">Exploration Hotspots:</h4>
            <ul>`;
          for (const h of hotspots) {
            const hTitle = String(h?.title || "");
            const hInfo = String(h?.info || "");
            html += `<li><strong>${escapeHtml(hTitle)}:</strong> ${escapeHtml(hInfo)}</li>`;
          }
          html += `</ul></div>`;
        }
        html += `</div>`;
        break;
      }
      case "workplace_scenario": {
        const gen = data?.generated as Record<string, unknown>;
        const scenarioMd =
          (gen && typeof gen.markdown === "string" && gen.markdown) ||
          (typeof data?.scenario === "string" && data.scenario) ||
          (typeof data?.content === "string" && data.content) ||
          (typeof data?.markdown === "string" && data.markdown) ||
          "";
        html += `<div class="pdf-block pdf-scenario-box">
          <div class="pdf-scenario-badge">Workplace Scenario</div>
          <div class="pdf-scenario-content">${renderMarkdown(scenarioMd || "Personalized workplace scenario applying lesson concepts.")}</div>
        </div>`;
        break;
      }
      default: {
        const anyMd = typeof data?.markdown === "string" ? data.markdown : typeof data?.content === "string" ? data.content : "";
        if (anyMd.trim()) {
          html += `<div class="pdf-block pdf-prose">${renderMarkdown(anyMd)}</div>`;
        }
        break;
      }
    }
  }

  html += `</div></div>`;
  return html;
}

function buildProseLessonHtml(
  data: ProfessionalProactiveStructuredData | ProactiveStructuredData,
): string {
  const sections = [...data.sections].sort((a, b) => a.section_number - b.section_number);
  const objectives = data.personalization_summary?.learning_objectives || [];
  const ws = (data as unknown as Record<string, unknown>).workplace_scenario;
  const duc = (data as unknown as Record<string, unknown>).domain_use_case;
  const wsObj = ws && typeof ws === "object" ? (ws as Record<string, unknown>) : null;
  const ducObj = duc && typeof duc === "object" ? (duc as Record<string, unknown>) : null;
  const scenarioText =
    (typeof wsObj?.scenario === "string" && wsObj.scenario) ||
    (typeof wsObj?.content === "string" && wsObj.content) ||
    (typeof ws === "string" && ws) ||
    (typeof ducObj?.scenario === "string" && ducObj.scenario) ||
    "";
  const reflection = typeof (data as unknown as Record<string, unknown>).reflection_prompt === "string" ? (data as unknown as Record<string, unknown>).reflection_prompt as string : "";

  let html = `<div class="pdf-prose-content">`;
  if (objectives.length > 0) {
    html += `<div class="pdf-objectives-box">
      <h3 class="pdf-obj-title">Learning Objectives</h3>
      <ul>`;
    for (const obj of objectives) {
      html += `<li>${escapeHtml(String(obj))}</li>`;
    }
    html += `</ul></div>`;
  }

  for (const s of sections) {
    html += `<div class="pdf-section">
      <h2 class="pdf-section-title">Part ${s.section_number}: ${escapeHtml(s.heading)}</h2>
      <div class="pdf-prose">${renderMarkdown(s.content || "")}</div>
    </div>`;
  }

  if (scenarioText.trim()) {
    html += `<div class="pdf-section">
      <div class="pdf-scenario-box">
        <div class="pdf-scenario-badge">Workplace Scenario</div>
        <div class="pdf-scenario-content">${renderMarkdown(scenarioText)}</div>
      </div>
    </div>`;
  }

  if (reflection.trim()) {
    html += `<div class="pdf-reflection-box">
      <h3 class="pdf-ref-title">Reflect</h3>
      <p><em>${escapeHtml(reflection)}</em></p>
    </div>`;
  }

  html += `</div>`;
  return html;
}

export function buildFullLessonHtml(params: LessonPdfExportParams): string {
  const {
    courseTitle,
    lessonTitle,
    moduleTitle,
    industryDomain,
    language = "English",
    blocksContent,
    structuredContent,
  } = params;

  let bodyContent = "";
  if (blocksContent?.sections?.length) {
    bodyContent = blocksContent.sections.map((s, idx) => buildSectionBlocksHtml(s, idx)).join("\n");
  } else if (isProfessionalProactiveStructuredData(structuredContent)) {
    bodyContent = buildProseLessonHtml(structuredContent);
  } else if (isProactiveStructuredData(structuredContent)) {
    bodyContent = buildProseLessonHtml(structuredContent);
  } else if (typeof structuredContent === "string") {
    bodyContent = `<div class="pdf-prose">${renderMarkdown(structuredContent)}</div>`;
  }

  return `
  <div class="pdf-export-document">
    <style>
      .pdf-export-document {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        color: #1e293b;
        background: #ffffff;
        padding: 40px 48px;
        line-height: 1.6;
        font-size: 13.5px;
        max-width: 820px;
        margin: 0 auto;
        box-sizing: border-box;
      }
      .pdf-header {
        border-bottom: 2px solid #e2e8f0;
        padding-bottom: 18px;
        margin-bottom: 28px;
      }
      .pdf-org-tag {
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        color: #4f46e5;
        margin-bottom: 4px;
      }
      .pdf-lesson-title {
        font-size: 24px;
        font-weight: 800;
        color: #0f172a;
        margin: 0 0 6px 0;
        line-height: 1.25;
      }
      .pdf-meta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
        margin-top: 10px;
      }
      .pdf-meta-badge {
        background: #f1f5f9;
        color: #334155;
        border-radius: 5px;
        padding: 3px 9px;
        font-size: 11px;
        font-weight: 600;
      }
      .pdf-section {
        margin-bottom: 30px;
        page-break-inside: avoid;
      }
      .pdf-section-title {
        font-size: 17px;
        font-weight: 700;
        color: #0f172a;
        border-bottom: 1.5px solid #e2e8f0;
        padding-bottom: 6px;
        margin-bottom: 14px;
      }
      .pdf-block {
        margin-bottom: 16px;
      }
      .pdf-prose p {
        margin-top: 0;
        margin-bottom: 10px;
      }
      .pdf-prose ul, .pdf-prose ol {
        margin-top: 4px;
        margin-bottom: 10px;
        padding-left: 20px;
      }
      .pdf-prose h1, .pdf-prose h2, .pdf-prose h3 {
        color: #0f172a;
        margin-top: 14px;
        margin-bottom: 8px;
      }
      .pdf-prose strong {
        color: #0f172a;
      }
      .pdf-card-group {
        border: 1px solid #e2e8f0;
        background: #f8fafc;
        border-radius: 8px;
        padding: 14px;
      }
      .pdf-card-prompt {
        font-size: 12.5px;
        color: #334155;
        margin: 0 0 10px 0;
      }
      .pdf-cards-list {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
      }
      .pdf-card-item {
        border: 1px solid #cbd5e1;
        background: #ffffff;
        border-radius: 6px;
        padding: 10px;
      }
      .pdf-card-term {
        font-size: 12.5px;
        color: #1e293b;
        margin-bottom: 4px;
      }
      .pdf-card-desc {
        font-size: 11.5px;
        color: #475569;
      }
      .pdf-quiz-box {
        border: 1px solid #cbd5e1;
        background: #f8fafc;
        border-radius: 8px;
        padding: 14px;
      }
      .pdf-quiz-prompt {
        font-size: 13px;
        color: #0f172a;
        margin-top: 0;
        margin-bottom: 8px;
      }
      .pdf-quiz-options {
        list-style: none;
        padding-left: 0;
        margin: 0 0 8px 0;
      }
      .pdf-quiz-options li {
        font-size: 12px;
        padding: 4px 8px;
        border-radius: 4px;
        margin-bottom: 4px;
      }
      .pdf-opt-correct {
        background: #ecfdf5;
        color: #065f46;
        font-weight: 600;
      }
      .pdf-quiz-exp {
        border-top: 1px solid #e2e8f0;
        padding-top: 8px;
        font-size: 12px;
        color: #334155;
      }
      .pdf-cat-box, .pdf-seq-box {
        border: 1px solid #e2e8f0;
        background: #f8fafc;
        border-radius: 8px;
        padding: 14px;
      }
      .pdf-cat-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        margin-top: 8px;
      }
      .pdf-cat-col {
        border: 1px solid #cbd5e1;
        background: #ffffff;
        border-radius: 6px;
        padding: 10px;
      }
      .pdf-cat-name {
        font-size: 12px;
        font-weight: 700;
        color: #4f46e5;
        margin: 0 0 6px 0;
      }
      .pdf-cat-col ul {
        margin: 0;
        padding-left: 16px;
        font-size: 11.5px;
      }
      .pdf-scenario-box {
        border: 2px solid #10b981;
        background: #f0fdf4;
        border-radius: 10px;
        padding: 18px;
        margin-top: 12px;
      }
      .pdf-scenario-badge {
        display: inline-block;
        background: #059669;
        color: #ffffff;
        font-size: 10px;
        font-weight: 800;
        text-transform: uppercase;
        padding: 3px 8px;
        border-radius: 9999px;
        margin-bottom: 10px;
      }
      .pdf-objectives-box {
        border: 1.5px solid #c7d2fe;
        background: #eef2ff;
        border-radius: 10px;
        padding: 16px;
        margin-bottom: 24px;
      }
      .pdf-obj-title {
        font-size: 13px;
        font-weight: 700;
        color: #3730a3;
        margin: 0 0 8px 0;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      .pdf-objectives-box ul {
        margin: 0;
        padding-left: 18px;
        font-size: 12.5px;
        color: #1e293b;
      }
      .pdf-reflection-box {
        border: 1.5px solid #ddd6fe;
        background: #faf5ff;
        border-radius: 10px;
        padding: 16px;
        margin-top: 20px;
      }
      .pdf-ref-title {
        font-size: 13px;
        font-weight: 700;
        color: #6b21a8;
        margin: 0 0 6px 0;
        text-transform: uppercase;
      }
      .pdf-image-box {
        margin: 12px 0;
      }
      .pdf-fill-blank-box {
        border: 1px solid #c7d2fe;
        background: #f5f7ff;
        border-radius: 8px;
        padding: 14px;
      }
      .pdf-fill-prompt {
        font-size: 13px;
        color: #3730a3;
        margin-top: 0;
        margin-bottom: 8px;
      }
      .pdf-fill-body {
        font-size: 13px;
        color: #1e293b;
        line-height: 1.7;
      }
      .pdf-fill-keys {
        margin-top: 10px;
        padding-top: 8px;
        border-top: 1px dashed #c7d2fe;
        font-size: 11.5px;
        color: #4338ca;
      }
      .pdf-accordion-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .pdf-accordion-item {
        border: 1px solid #e2e8f0;
        background: #f8fafc;
        border-radius: 8px;
        padding: 12px 14px;
      }
      .pdf-accordion-title {
        font-size: 13.5px;
        font-weight: 700;
        color: #1e293b;
        margin: 0 0 6px 0;
      }
      .pdf-timeline-box {
        border: 1px solid #e2e8f0;
        background: #f8fafc;
        border-radius: 8px;
        padding: 14px;
      }
      .pdf-timeline-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .pdf-timeline-item {
        display: flex;
        gap: 12px;
        align-items: flex-start;
      }
      .pdf-timeline-num {
        flex-shrink: 0;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #4f46e5;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        margin-top: 1px;
      }
      .pdf-timeline-content {
        flex: 1;
      }
      .pdf-timeline-title {
        font-size: 13px;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 4px 0;
      }
      .pdf-hotspot-box {
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 12px;
        background: #ffffff;
      }
      .pdf-hotspot-img {
        max-width: 100%;
        height: auto;
        border-radius: 6px;
        margin-bottom: 10px;
        display: block;
      }
      .pdf-hotspot-list {
        background: #f8fafc;
        border-radius: 6px;
        padding: 10px 14px;
      }
      .pdf-hotspot-header {
        font-size: 12px;
        font-weight: 700;
        color: #334155;
        margin: 0 0 6px 0;
      }
      .pdf-hotspot-list ul {
        margin: 0;
        padding-left: 18px;
        font-size: 12px;
      }
    </style>

    <header class="pdf-header">
      <div class="pdf-org-tag">INARA STUDY GUIDE • ${escapeHtml(courseTitle)}</div>
      <h1 class="pdf-lesson-title">${escapeHtml(lessonTitle)}</h1>
      <div class="pdf-meta-row">
        ${moduleTitle ? `<span class="pdf-meta-badge">Module: ${escapeHtml(moduleTitle)}</span>` : ""}
        ${industryDomain ? `<span class="pdf-meta-badge">Focus: ${escapeHtml(industryDomain)}</span>` : ""}
        <span class="pdf-meta-badge">Language: ${escapeHtml(language)}</span>
      </div>
    </header>

    <main class="pdf-body">
      ${bodyContent}
    </main>
  </div>
  `;
}

export async function exportFullLessonToPdf(
  params: LessonPdfExportParams,
  filename: string,
): Promise<void> {
  const html2canvas = (await import("html2canvas")).default;
  const { jsPDF } = await import("jspdf");

  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-99999px";
  container.style.top = "0";
  container.style.width = "820px";
  container.style.backgroundColor = "#ffffff";
  container.style.zIndex = "-9999";
  container.innerHTML = buildFullLessonHtml(params);
  document.body.appendChild(container);

  try {
    if (typeof document !== "undefined" && document.fonts?.ready) {
      await document.fonts.ready;
    }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const width = container.scrollWidth || 820;
    const height = container.scrollHeight;

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: "#ffffff",
      width,
      height,
      windowWidth: width,
      windowHeight: height,
    });

    const imgData = canvas.toDataURL("image/png", 1.0);
    const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
}

export async function exportLessonRootToPdf(
  root: HTMLElement,
  filename: string,
): Promise<void> {
  const html2canvas = (await import("html2canvas")).default;
  const { jsPDF } = await import("jspdf");

  const scroll = root.querySelector<HTMLElement>("[data-lesson-pdf-scroll]");
  const savedScrollTop = scroll?.scrollTop ?? 0;

  const rootBackup = { overflow: root.style.overflow, height: root.style.height, minHeight: root.style.minHeight };
  const scrollBackup = scroll
    ? {
        overflow: scroll.style.overflow,
        height: scroll.style.height,
        maxHeight: scroll.style.maxHeight,
        minHeight: scroll.style.minHeight,
      }
    : null;

  root.style.overflow = "visible";
  root.style.height = "auto";
  root.style.minHeight = "auto";

  if (scroll && scrollBackup) {
    scroll.style.overflow = "visible";
    scroll.style.maxHeight = "none";
    scroll.style.height = `${scroll.scrollHeight}px`;
  }

  try {
    if (typeof document !== "undefined" && document.fonts?.ready) {
      await document.fonts.ready;
    }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const width = root.scrollWidth;
    const height = root.scrollHeight;

    const canvas = await html2canvas(root, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: "#ffffff",
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      scrollX: 0,
      scrollY: 0,
    });

    const imgData = canvas.toDataURL("image/png", 1.0);
    const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(filename);
  } finally {
    root.style.overflow = rootBackup.overflow;
    root.style.height = rootBackup.height;
    root.style.minHeight = rootBackup.minHeight;
    if (scroll && scrollBackup) {
      scroll.style.overflow = scrollBackup.overflow;
      scroll.style.height = scrollBackup.height;
      scroll.style.maxHeight = scrollBackup.maxHeight;
      scroll.style.minHeight = scrollBackup.minHeight;
      scroll.scrollTop = savedScrollTop;
    }
  }
}

